import {makeStyles} from "@material-ui/core/styles";

export const BRAND = {
    deep: "#2c4767",
    deepDark: "#1e3553",
    gold: "#b08d57",
    goldSoft: "rgba(176, 141, 87, 0.18)",
    goldBg: "rgba(176, 141, 87, 0.10)",
    cream: "#f7f4ee",
    creamSoft: "#fbfaf7",
    ink: "#1a2733",
    inkSoft: "#5a6675",
    success: "#2f6147",
    successBg: "rgba(61, 122, 90, 0.10)",
    successBorder: "rgba(61, 122, 90, 0.22)",
};

export const useDashboardStyles = makeStyles((theme) => ({
    pageContainer: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(6),
        [theme.breakpoints.down("xs")]: {
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
    },
    paper: {
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        borderRadius: 14,
        border: `1px solid ${BRAND.goldSoft}`,
        boxShadow: "0 6px 22px rgba(44, 71, 103, 0.06)",
        position: "relative",
        overflow: "visible",
        [theme.breakpoints.down("xs")]: {
            padding: theme.spacing(2.5),
            borderRadius: 10,
        },
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${BRAND.deep} 0%, ${BRAND.gold} 100%)`,
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
        },
    },
    sectionHeader: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
        [theme.breakpoints.down("xs")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    sectionToolbar: {
        marginLeft: "auto",
        padding: 0,
        minHeight: "auto",
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        [theme.breakpoints.down("xs")]: {
            marginLeft: 0,
            justifyContent: "flex-start",
        },
    },
    sectionDivider: {
        margin: theme.spacing(0, 0, 3, 0),
        height: 1,
        border: 0,
        background: `linear-gradient(90deg, ${BRAND.goldSoft} 0%, ${BRAND.goldSoft} 100%)`,
    },
    emptyState: {
        padding: theme.spacing(4, 3),
        textAlign: "center",
        background: BRAND.creamSoft,
        border: `1px dashed ${BRAND.goldSoft}`,
        borderRadius: 10,
        color: BRAND.inkSoft,
        fontSize: "0.95rem",
    },
    primaryAction: {
        background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.deepDark} 100%)`,
        color: "#ffffff",
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.92rem",
        padding: theme.spacing(0.9, 2),
        borderRadius: 8,
        boxShadow: `0 4px 12px rgba(44, 71, 103, 0.20)`,
        transition: "background 180ms ease, box-shadow 200ms ease, transform 160ms ease",
        "&:hover": {
            background: `linear-gradient(135deg, ${BRAND.deepDark} 0%, ${BRAND.deep} 100%)`,
            boxShadow: "0 6px 18px rgba(44, 71, 103, 0.30)",
            transform: "translateY(-1px)",
        },
    },
    secondaryAction: {
        background: "#ffffff",
        color: BRAND.deep,
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.92rem",
        padding: theme.spacing(0.9, 2),
        border: `1.5px solid ${BRAND.goldSoft}`,
        borderRadius: 8,
        transition: "background 180ms ease, border-color 180ms ease",
        "&:hover": {
            background: BRAND.goldBg,
            borderColor: BRAND.gold,
        },
    },
    /* Read-only TextField → definition row */
    detailView: {
        "& .MuiFormControl-root.MuiTextField-root": {
            margin: 0,
            marginBottom: theme.spacing(1.5),
            padding: theme.spacing(1.5, 2),
            background: BRAND.creamSoft,
            border: `1px solid ${BRAND.goldSoft}`,
            borderRadius: 10,
            transition: "border-color 150ms ease, background 150ms ease, box-shadow 150ms ease",
            width: "100%",
            boxSizing: "border-box",
        },
        "& .MuiFormControl-root.MuiTextField-root:hover": {
            background: "#ffffff",
            borderColor: "rgba(176, 141, 87, 0.35)",
            boxShadow: "0 2px 8px rgba(44, 71, 103, 0.04)",
        },
        "& .MuiInputLabel-root": {
            position: "relative",
            transform: "none !important",
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: BRAND.inkSoft,
            marginBottom: 6,
            display: "block",
            pointerEvents: "none",
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: BRAND.deep,
        },
        "& .MuiInput-underline::before, & .MuiInput-underline::after": {
            display: "none",
        },
        "& .MuiInputBase-root": {
            marginTop: "0 !important",
            padding: 0,
            background: "transparent",
            minHeight: 24,
        },
        "& .MuiInputBase-root.Mui-readOnly": {
            cursor: "default",
        },
        "& .Mui-readOnly input, & .Mui-readOnly textarea": {
            cursor: "default",
            fontSize: "1rem",
            fontWeight: 500,
            color: BRAND.ink,
            padding: 0,
            lineHeight: 1.55,
            WebkitTextFillColor: BRAND.ink,
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
        },
        "& .MuiInputAdornment-root": {
            display: "none",
        },
    },
    /* Template card (services / products / tasks templates) */
    templateCard: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#ffffff",
        border: `1px solid ${BRAND.goldSoft}`,
        borderRadius: 12,
        boxShadow: "0 4px 14px rgba(44, 71, 103, 0.05)",
        transition: "box-shadow 220ms ease, transform 200ms ease, border-color 220ms ease",
        overflow: "hidden",
        "&:hover": {
            boxShadow: "0 12px 28px rgba(44, 71, 103, 0.10)",
            borderColor: "rgba(176, 141, 87, 0.45)",
        },
    },
    templateCardBody: {
        flex: "1 1 auto",
        padding: theme.spacing(2, 2.25, 1.5, 2.25),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
        minHeight: 0,
    },
    templateNameRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    templateName: {
        fontWeight: 700,
        fontSize: "1.02rem",
        color: BRAND.ink,
        lineHeight: 1.3,
        wordBreak: "break-word",
        margin: 0,
        flex: "1 1 auto",
    },
    templateTypeBadge: {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 9px",
        background: BRAND.goldBg,
        color: BRAND.deep,
        borderRadius: 999,
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        border: `1px solid ${BRAND.goldSoft}`,
        width: "fit-content",
    },
    templateChipBox: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1),
        maxHeight: 120,
        overflowY: "auto",
    },
    templateChip: {
        background: BRAND.creamSoft,
        border: `1px solid ${BRAND.goldSoft}`,
        color: BRAND.deep,
        fontWeight: 500,
        height: 26,
        "& .MuiChip-label": {
            fontSize: "0.8rem",
        },
    },
    templateCardActions: {
        padding: theme.spacing(1, 1.5),
        background: BRAND.creamSoft,
        borderTop: `1px solid rgba(176, 141, 87, 0.14)`,
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(0.5),
    },
    formField: {
        marginBottom: theme.spacing(2),
        width: "100%",
    },
    formActions: {
        marginTop: theme.spacing(3),
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
    },
    optionsCard: {
        marginTop: theme.spacing(2),
        background: BRAND.creamSoft,
        border: `1px solid ${BRAND.goldSoft}`,
        borderRadius: 10,
        padding: theme.spacing(2),
    },
    optionsTitle: {
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: BRAND.inkSoft,
        marginBottom: theme.spacing(1.5),
    },
    optionsChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.75),
        marginBottom: theme.spacing(1.5),
        minHeight: 32,
    },
    optionsInputs: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1.5),
        alignItems: "flex-end",
        "& .MuiFormControl-root": {
            flex: "1 1 200px",
        },
    },
}));
